import { PopupButtonType, PopupIconType, showPopup } from "@kalyzee/kast-app-web-components";
import React, { createRef, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageContainer from "../../components/page/PageContainer";
import { CustomCheckBox } from "../../components/utils/CustomCheckBox";
import Select from "../../components/utils/Select";
import { OvenMediaSocketResultEmittedCommand } from "../../helpers/socket";
import { toastError, toastSuccess } from "../../helpers/toast";
import { useSocketAppDispatch } from "../../hooks/app";
import {
  GetOmeApplicationsResponse,
  OmeApplication,
  OmeApplicationApplyOutcome,
  OmeApplicationImportStatus,
  OmeApplicationLintResult,
  OmeApplicationMode,
  OmeApplicationRevisionEntry,
} from "../../interfaces/omeApplication";
import { useOmeApplications, useOmeApplicationsLoaded } from "../../store/omeApplication/hooks";
import { useStreamProfiles } from "../../store/streamProfile/hooks";
import {
  socketApplyOmeApplication,
  socketDeleteOmeApplication,
  socketGetLiveSessions,
  socketGetOmeApplicationRevisions,
  socketGetOmeApplications,
  socketGetRecordSessions,
  socketGetStreamProfiles,
  socketLintOmeApplication,
  socketRetryImportOmeApplication,
  socketUpsertOmeApplication,
} from "../../store/socket/actions";
import AppConfigEditor from "./containers/app-config-editor";
import styles from "./app-settings.module.css";

const isPending = (app: OmeApplication) => app.revision > (app.appliedRevision ?? 0);

const AppSettingsPage = () => {
  const socketDispatch = useSocketAppDispatch();
  const navigate = useNavigate();
  const applications = useOmeApplications();
  const streamProfiles = useStreamProfiles();
  const loaded = useOmeApplicationsLoaded();
  const { application: applicationParam } = useParams();

  const [selectedName, setSelectedName] = useState<string>();
  const [draftConfig, setDraftConfig] = useState<any>();
  const [draftNotes, setDraftNotes] = useState<string>("");
  const [draftDefaultProfile, setDraftDefaultProfile] = useState<string>("");
  const [draftRequireDefaultProfile, setDraftRequireDefaultProfile] = useState<boolean>(false);
  const [lintResults, setLintResults] = useState<OmeApplicationLintResult[]>();
  const [busy, setBusy] = useState(false);

  const selected = applications.find((app) => app.name === selectedName);

  useEffect(() => {
    socketDispatch(socketGetOmeApplications());
    socketDispatch(socketGetStreamProfiles());
  }, []);

  const openEditor = (app: OmeApplication) => {
    setSelectedName(app.name);
    setDraftConfig(JSON.parse(JSON.stringify(app.config ?? { name: app.name, type: "live" })));
    setDraftNotes(app.notes ?? "");
    setDraftDefaultProfile(app.defaultProfile ?? "");
    setDraftRequireDefaultProfile(app.requireDefaultProfile ?? false);
    setLintResults(undefined);
  };

  // A draft whose name is not (yet) in the store: composing a new application
  // on the list route, or a just-saved one whose broadcast hasn't landed.
  const isNewDraft = !!selectedName && !applications.some((app) => app.name === selectedName);

  const openNewEditor = (name: string) => {
    setSelectedName(name);
    setDraftConfig({ name, type: "live" });
    setDraftNotes("");
    setDraftDefaultProfile("");
    setDraftRequireDefaultProfile(false);
    setLintResults(undefined);
  };

  const closeEditor = () => {
    setSelectedName(undefined);
    setDraftConfig(undefined);
    setLintResults(undefined);
  };

  // The route param drives the view: /app-settings/<name> (menu sub-items,
  // "Edit") shows only that application, editor open when managed; no param is
  // the list. Deliberately not re-run on store updates so a draft being edited
  // is not reset by a background refresh. The list and detail routes render
  // this same component, so clearing the param must close the editor here.
  useEffect(() => {
    if (!loaded) return;
    const app = applicationParam ? applications.find((current) => current.name === applicationParam) : undefined;
    if (app && app.mode === OmeApplicationMode.MANAGED) openEditor(app);
    else if (app) closeEditor();
    // No store match: keep a draft that matches the param (a just-created app
    // can reach its detail route before the broadcast lands); anything else
    // (param cleared, bogus URL) drops the draft.
    else if (selectedName !== applicationParam) closeEditor();
  }, [applicationParam, loaded]);

  const runLint = async () => {
    const result = await socketDispatch<OvenMediaSocketResultEmittedCommand>(socketLintOmeApplication({ config: draftConfig }));
    if (result?.error) toastError(result.error.message);
    else setLintResults(result?.response?.lintResults ?? []);
  };

  const save = async (): Promise<boolean> => {
    if (!selectedName || !draftConfig) return false;
    const wasNew = isNewDraft;
    setBusy(true);
    const result = await socketDispatch<OvenMediaSocketResultEmittedCommand>(
      socketUpsertOmeApplication({
        name: selectedName,
        config: draftConfig,
        notes: draftNotes,
        // "" = explicit "— none —" (the middleware $unsets it); undefined would
        // mean "leave untouched" and the old default would silently survive.
        defaultProfile: draftDefaultProfile,
        requireDefaultProfile: draftRequireDefaultProfile,
      })
    );
    setBusy(false);
    if (result?.error) {
      toastError(result.error.message);
      return false;
    }
    setLintResults(result?.response?.lintResults ?? []);
    toastSuccess("Saved — apply to load it into the engine" /* TRANSLATION */);
    // A created application continues on its own detail route (the draft
    // carries over until the store broadcast lands).
    if (wasNew) navigate(`/app-settings/${selectedName}`);
    return true;
  };

  const runApply = async (name: string, include: string[]) => {
    setBusy(true);
    const result = await socketDispatch<OvenMediaSocketResultEmittedCommand<any, OmeApplicationApplyOutcome>>(
      socketApplyOmeApplication({ name, include })
    );
    setBusy(false);
    socketDispatch(socketGetOmeApplications());
    const outcome: OmeApplicationApplyOutcome | undefined = (result?.response ?? (result?.error as any)?.content) as any;
    if (result?.error) {
      toastError(`Apply failed: ${(result.error as any)?.message ?? outcome?.error ?? "see the application banner"}` /* TRANSLATION */);
      return;
    }
    if (outcome?.verifyMismatches?.length) {
      toastError(`Applied, but the engine differs from the model for: ${outcome.verifyMismatches.join(", ")}` /* TRANSLATION */);
      return;
    }
    toastSuccess("Applied — engine restarted on the new config" /* TRANSLATION */);
  };

  const confirmApply = async (app: OmeApplication) => {
    // The #25 confirm: applying restarts the engine — every live stream and
    // record session on this host is interrupted; other pending apps can ride
    // the same restart.
    let liveCount = 0;
    let recordCount = 0;
    try {
      const live = await socketDispatch<OvenMediaSocketResultEmittedCommand>(socketGetLiveSessions({ status: "running" as any, limit: 1 }));
      liveCount = live?.response?.total ?? 0;
      const records = await socketDispatch<OvenMediaSocketResultEmittedCommand>(socketGetRecordSessions({ status: "running" as any, limit: 1 }));
      recordCount = records?.response?.total ?? 0;
    } catch {}

    const otherPending = applications.filter((other) => other.name !== app.name && other.mode === OmeApplicationMode.MANAGED && isPending(other));
    const includeRefs = otherPending.map(() => createRef<HTMLInputElement>());

    showPopup({
      title: `${"Apply & restart the engine" /* TRANSLATION */} — ${app.name} (rev ${app.revision})`,
      iconTitle: PopupIconType.INFO,
      enableBackdropDismiss: false,
      enableCloseButton: false,
      content: (
        <div className={styles.popupContent}>
          <div className={styles.popupWarning}>
            {"Applying restarts the media engine: every live stream and recording on this host is interrupted (producers must reconnect)." /* TRANSLATION */}
          </div>
          <div className={styles.popupCounts}>
            {`Live streams: ${liveCount} — active recordings: ${recordCount}` /* TRANSLATION */}
          </div>
          {otherPending.length ? (
            <div className={styles.popupInclude}>
              <div>{"Also apply pending changes of:" /* TRANSLATION */}</div>
              {otherPending.map((other, index) => (
                <label key={other.name} className={styles.popupIncludeItem}>
                  <input ref={includeRefs[index]} type="checkbox" defaultChecked />
                  {` ${other.name} (rev ${other.revision} → applied ${other.appliedRevision ?? "none"})`}
                </label>
              ))}
            </div>
          ) : null}
        </div>
      ),
      buttons: [
        { type: PopupButtonType.CANCEL, element: "Cancel" /* TRANSLATION */ },
        {
          type: PopupButtonType.VALIDATE,
          element: "Apply & restart" /* TRANSLATION */,
          onClick: () => {
            const include = otherPending.filter((_, index) => includeRefs[index].current?.checked).map((other) => other.name);
            runApply(app.name, include);
            return true;
          },
        },
      ],
    });
  };

  const confirmDelete = (app: OmeApplication) => {
    showPopup({
      title: `${"Delete application" /* TRANSLATION */} — ${app.name}`,
      iconTitle: PopupIconType.INFO,
      enableBackdropDismiss: false,
      enableCloseButton: false,
      content: (
        <div className={styles.popupContent}>
          <div className={styles.popupWarning}>
            {"Deleting restarts the media engine (same impact as an apply). The XML is archived next to the others as " /* TRANSLATION */}
            <code>{`${app.name}.xml.deleted-<timestamp>`}</code>
            {" — it can be restored from the revision history." /* TRANSLATION */}
          </div>
        </div>
      ),
      buttons: [
        { type: PopupButtonType.CANCEL, element: "Cancel" /* TRANSLATION */ },
        {
          type: PopupButtonType.VALIDATE,
          element: "Delete & restart" /* TRANSLATION */,
          onClick: () => {
            (async () => {
              setBusy(true);
              const result = await socketDispatch<OvenMediaSocketResultEmittedCommand>(socketDeleteOmeApplication({ name: app.name }));
              setBusy(false);
              socketDispatch(socketGetOmeApplications());
              if (result?.error) toastError(`Delete failed: ${(result.error as any)?.message ?? ""}` /* TRANSLATION */);
              else toastSuccess("Deleted — engine restarted" /* TRANSLATION */);
              if (applicationParam === app.name) navigate("/app-settings");
              else if (selectedName === app.name) closeEditor();
            })();
            return true;
          },
        },
      ],
    });
  };

  const retryImport = async (app: OmeApplication) => {
    const result = await socketDispatch<OvenMediaSocketResultEmittedCommand>(socketRetryImportOmeApplication({ name: app.name }));
    if (result?.error) toastError(`Import failed: ${(result.error as any)?.message ?? (result.error as any)?.content?.error ?? ""}` /* TRANSLATION */);
    else toastSuccess("Imported — the application is now managed" /* TRANSLATION */);
    socketDispatch(socketGetOmeApplications());
  };

  const showRevisions = async (app: OmeApplication) => {
    const result = await socketDispatch<OvenMediaSocketResultEmittedCommand>(socketGetOmeApplicationRevisions({ name: app.name, limit: 30 }));
    if (result?.error) {
      toastError(result.error.message);
      return;
    }
    const revisions: OmeApplicationRevisionEntry[] = result?.response?.revisions ?? [];
    showPopup({
      title: `${"Revision history" /* TRANSLATION */} — ${app.name}`,
      iconTitle: PopupIconType.INFO,
      content: (
        <div className={styles.revisionList}>
          {revisions.map((revision) => (
            <div key={revision._id} className={styles.revisionRow}>
              <span className={styles.revisionAction}>{revision.action}</span>
              <span>rev {revision.revision}</span>
              <span>{revision.createdAt ? new Date(revision.createdAt).toLocaleString() : ""}</span>
              <span>{revision.author ?? ""}</span>
              <span>{revision.applyResult ? (revision.applyResult.success ? "✓ applied" : `✗ ${revision.applyResult.error ?? "failed"}`) : ""}</span>
            </div>
          ))}
        </div>
      ),
      buttons: [{ type: PopupButtonType.OK, element: "OK" }],
    });
  };

  const renderBadges = (app: OmeApplication) => (
    <span className={styles.badges}>
      {app.mode === OmeApplicationMode.RAW ? <span className={styles.badgeRaw}>{"unmanaged" /* TRANSLATION */}</span> : null}
      {app.importStatus === OmeApplicationImportStatus.FAILED ? <span className={styles.badgeFailed}>import failed</span> : null}
      {app.importStatus === OmeApplicationImportStatus.UNSUPPORTED ? <span className={styles.badgeFailed}>unsupported</span> : null}
      {app.mode === OmeApplicationMode.MANAGED && isPending(app) ? (
        <span className={styles.badgePending}>{`pending rev ${app.revision}` /* TRANSLATION */}</span>
      ) : null}
      {app.xmlDrift ? <span className={styles.badgeDrift}>{"file hand-edited" /* TRANSLATION */}</span> : null}
      {app.applyStatus?.state === "failed" ? <span className={styles.badgeFailed}>{"apply FAILED" /* TRANSLATION */}</span> : null}
    </span>
  );

  const renderFailedBanner = (app: OmeApplication) => {
    if (app.applyStatus?.state !== "failed") return null;
    return (
      <div className={styles.failedBanner}>
        <div>
          <b>{"Last apply failed" /* TRANSLATION */}</b>
          {` — ${app.applyStatus.error ?? ""} (${app.applyStatus.at ? new Date(app.applyStatus.at).toLocaleString() : ""}). `}
          {"The previous XML was restored and the engine restarted on it; the change stays pending." /* TRANSLATION */}
        </div>
        {app.applyStatus.engineLogTail ? (
          <details>
            <summary>{"Engine log tail (failing boot)" /* TRANSLATION */}</summary>
            <pre className={styles.logTail}>{app.applyStatus.engineLogTail}</pre>
          </details>
        ) : null}
      </div>
    );
  };

  const renderAppHeader = (app: OmeApplication, showEdit: boolean) => (
    <div className={styles.appRow}>
      <span className={styles.appName}>{app.name}</span>
      {renderBadges(app)}
      <span className={styles.appActions}>
        {showEdit && app.mode === OmeApplicationMode.MANAGED ? (
          <button type="button" className={styles.actionButton} disabled={busy} onClick={() => navigate(`/app-settings/${app.name}`)}>
            {"Edit" /* TRANSLATION */}
          </button>
        ) : null}
        {app.mode === OmeApplicationMode.MANAGED && isPending(app) ? (
          <button type="button" className={styles.actionButton} disabled={busy} onClick={() => confirmApply(app)}>
            {"Apply" /* TRANSLATION */}
          </button>
        ) : null}
        {app.mode === OmeApplicationMode.RAW && app.importStatus === OmeApplicationImportStatus.FAILED ? (
          <button type="button" className={styles.actionButton} disabled={busy} onClick={() => retryImport(app)}>
            {"Retry import" /* TRANSLATION */}
          </button>
        ) : null}
        <button type="button" className={styles.actionButton} disabled={busy} onClick={() => showRevisions(app)}>
          {"History" /* TRANSLATION */}
        </button>
        <button type="button" className={styles.actionButtonDanger} disabled={busy} onClick={() => confirmDelete(app)}>
          {"Delete" /* TRANSLATION */}
        </button>
      </span>
    </div>
  );

  const renderRawBanner = (app: OmeApplication) =>
    app.mode === OmeApplicationMode.RAW ? (
      <div className={styles.rawBanner}>
        {app.importStatus === OmeApplicationImportStatus.UNSUPPORTED
          ? "This XML uses constructs the managed model cannot represent — it stays under manual (file) control." /* TRANSLATION */
          : "This XML could not be imported — fix the file on disk, then retry the import. The engine keeps booting from the file as-is." /* TRANSLATION */}
      </div>
    ) : null;

  // app is absent while the application only exists as a draft (composing a
  // new one, or just saved and the broadcast not landed): no "Save & apply…"
  // (apply needs the store-backed application) and Close follows the route.
  const renderEditorBody = (app?: OmeApplication) => {
    if (!draftConfig) return null;
    // Admission-only defaults (kept out of AppConfigEditor: they are NOT engine
    // config, so they never round-trip into XML nor trigger apply/restart).
    const selectedProfile = streamProfiles.find((profile) => profile.name === draftDefaultProfile);
    const defaultProfileWarn =
      selectedProfile?.push?.sourceApp && selectedProfile.push.sourceApp !== selectedName
        ? `Profile "${draftDefaultProfile}" sources app "${selectedProfile.push.sourceApp}", not "${selectedName}" — the default resolves against the app actually pushed into.` /* TRANSLATION */
        : undefined;
    return (
      <div className={styles.editor}>
        <AppConfigEditor config={draftConfig} onChange={setDraftConfig} lintResults={lintResults} />
        <div className={styles.notesRow}>
          <span className={styles.notesLabel}>{"Notes" /* TRANSLATION */}</span>
          <textarea className={styles.notesInput} value={draftNotes} onChange={(event) => setDraftNotes(event.currentTarget.value)} />
        </div>
        <div className={styles.notesRow}>
          <span className={styles.notesLabel}>{"Default stream profile" /* TRANSLATION */}</span>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
            <Select
              value={draftDefaultProfile}
              options={streamProfiles.map((profile) => ({ value: profile.name, label: profile.name }))}
              placeholder={"— none (bare URLs publish plainly) —" /* TRANSLATION */}
              onValue={setDraftDefaultProfile}
            />
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <CustomCheckBox isSelected={draftRequireDefaultProfile} setSelected={setDraftRequireDefaultProfile} />
              <span>{"Require a default — reject bare (identity-only) URLs when none is set" /* TRANSLATION */}</span>
            </label>
            {defaultProfileWarn ? <div className={styles.rawBanner}>{defaultProfileWarn}</div> : null}
          </div>
        </div>
        <div className={styles.editorButtons}>
          <button type="button" className={styles.actionButton} disabled={busy} onClick={runLint}>
            {"Lint" /* TRANSLATION */}
          </button>
          <button type="button" className={styles.actionButton} disabled={busy} onClick={save}>
            {"Save" /* TRANSLATION */}
          </button>
          {app ? (
            <button
              type="button"
              className={styles.actionButton}
              disabled={busy}
              onClick={async () => {
                if (await save()) confirmApply(app);
              }}
            >
              {"Save & apply…" /* TRANSLATION */}
            </button>
          ) : null}
          <button
            type="button"
            className={styles.actionButton}
            disabled={busy}
            onClick={() => (applicationParam ? navigate("/app-settings") : closeEditor())}
          >
            {"Close" /* TRANSLATION */}
          </button>
        </div>
      </div>
    );
  };

  const promptNewApplication = () => {
    let input: HTMLInputElement | null = null;
    showPopup({
      title: "New application" /* TRANSLATION */,
      iconTitle: PopupIconType.INFO,
      content: (
        <div className={styles.popupContent}>
          <div>
            {"The application is created as a draft: configure it, Save, then Apply to load it into the engine." /* TRANSLATION */}
          </div>
          <input
            ref={(element) => {
              input = element;
            }}
            className={styles.nameInput}
            placeholder="my-app"
          />
        </div>
      ),
      buttons: [
        { type: PopupButtonType.CANCEL, element: "Cancel" /* TRANSLATION */ },
        {
          type: PopupButtonType.VALIDATE,
          element: "Create draft" /* TRANSLATION */,
          onClick: () => {
            const name = input?.value.trim() ?? "";
            if (!/^[a-zA-Z0-9_-]+$/.test(name)) {
              toastError("Invalid name — letters, digits, - and _ only" /* TRANSLATION */);
              return false;
            }
            if (applications.some((app) => app.name === name)) {
              toastError(`"${name}" already exists` /* TRANSLATION */);
              return false;
            }
            openNewEditor(name);
            return true;
          },
        },
      ],
    });
  };

  const renderBackLink = () => (
    <button type="button" className={styles.backLink} onClick={() => navigate("/app-settings")}>
      {"← All applications" /* TRANSLATION */}
    </button>
  );

  // List route: every application as a compact row (badges + actions), no
  // editor — unless a new application is being composed, which takes over the
  // page. Detail route: only the routed application, editor open.
  const renderList = () => {
    if (isNewDraft && draftConfig) {
      return (
        <div className={styles.appBlock}>
          <div className={styles.appRow}>
            <span className={styles.appName}>{selectedName}</span>
            <span className={styles.badges}>
              <span className={styles.badgePending}>{"new — not saved yet" /* TRANSLATION */}</span>
            </span>
          </div>
          {renderEditorBody()}
        </div>
      );
    }
    return (
      <>
        <div className={styles.toolbar}>
          <button type="button" className={styles.actionButton} disabled={busy} onClick={promptNewApplication}>
            {"+ New application" /* TRANSLATION */}
          </button>
        </div>
        {applications.map((app) => (
          <div key={app.name} className={styles.appBlock}>
            {renderAppHeader(app, true)}
            {renderRawBanner(app)}
            {renderFailedBanner(app)}
          </div>
        ))}
      </>
    );
  };

  const renderDetail = () => {
    const app = applications.find((current) => current.name === applicationParam);
    // A just-created application can reach this route before the store
    // broadcast lands — the surviving draft stands in until then.
    const draftMatches = !!draftConfig && selectedName === applicationParam;
    if (!app && !draftMatches) {
      return (
        <>
          {renderBackLink()}
          <div className={styles.notFound}>
            {`Application "${applicationParam}" was not found — it may have been deleted or renamed.` /* TRANSLATION */}
          </div>
        </>
      );
    }
    return (
      <>
        {renderBackLink()}
        <div className={styles.appBlock}>
          {app ? (
            <>
              {renderAppHeader(app, false)}
              {renderRawBanner(app)}
              {renderFailedBanner(app)}
            </>
          ) : (
            <div className={styles.appRow}>
              <span className={styles.appName}>{applicationParam}</span>
            </div>
          )}
          {app ? (selectedName === app.name ? renderEditorBody(app) : null) : renderEditorBody()}
        </div>
      </>
    );
  };

  return (
    <PageContainer
      title={applicationParam ? `${"App settings" /* TRANSLATION */} — ${applicationParam}` : "App settings" /* TRANSLATION */}
      loading={!loaded}
    >
      <div className={styles.container}>{applicationParam ? renderDetail() : renderList()}</div>
    </PageContainer>
  );
};

export default AppSettingsPage;
